import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { pretty } from "@/lib/helpers";
import { useFeatures } from "@/lib/useFeatures";
import { useRealtimeOrders } from "@/lib/useRealtimeOrders";
import { playChime } from "@/lib/chime";
import { printReceipt } from "@/lib/receipt";
import { FeatureLocked } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, AlertTriangle, Clock, ChefHat, Printer } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { key: "NEW", label: "New", statuses: ["NEW", "ACCEPTED", "ALLERGY_REVIEW_REQUIRED", "NEEDS_REVIEW"], next: "PREPARING", cta: "Start" },
  { key: "PREPARING", label: "Preparing", statuses: ["PREPARING"], next: "READY", cta: "Mark ready" },
  { key: "READY", label: "Ready", statuses: ["READY"], next: "COMPLETED", cta: "Complete" },
];

function fmt(ms) {
  const neg = ms < 0;
  const s = Math.floor(Math.abs(ms) / 1000);
  const m = Math.floor(s / 60);
  return `${neg ? "-" : ""}${m}:${String(s % 60).padStart(2, "0")}`;
}

function Ticket({ order, prepMinutes, now, onAdvance, cta, onPrint }) {
  const created = new Date(order.created_at).getTime();
  const target = created + prepMinutes * 60000;
  const remaining = target - now;
  const overdue = remaining < 0;
  const declared = order.declared_allergens || [];
  const allergyNote = order.allergy_dietary_note || order.customer_allergy_note;
  return (
    <div data-testid={`kds-ticket-${order.id}`} className="rounded-xl bg-slate-800 border border-slate-700 p-4 mb-3">
      <div className="flex items-center justify-between">
        <span className="font-bold text-white text-lg">{order.order_number}</span>
        <div className="flex items-center gap-2">
          <button data-testid={`kds-print-${order.id}`} onClick={() => onPrint(order)} title="Print ticket"
            className="text-slate-300 hover:text-white p-1 rounded hover:bg-slate-700"><Printer className="h-4 w-4" /></button>
          <span data-testid={`kds-timer-${order.id}`} className={`flex items-center gap-1 font-mono text-sm px-2 py-0.5 rounded ${overdue ? "bg-red-500/20 text-red-300" : "bg-emerald-500/20 text-emerald-300"}`}>
            <Clock className="h-3.5 w-3.5" /> {fmt(remaining)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-1">
        <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">
          {pretty(order.order_type)}{order.fulfillment_type ? ` · ${pretty(order.fulfillment_type)}` : ""}
        </Badge>
        {(declared.length > 0 || allergyNote) && (
          <span className="flex items-center gap-1 text-xs text-red-300"><AlertTriangle className="h-3.5 w-3.5" /> Allergy</span>
        )}
      </div>
      {declared.length > 0 && (
        <div className="mt-2 rounded-lg bg-red-500/15 border border-red-500/40 px-2.5 py-1.5" data-testid={`kds-declared-${order.id}`}>
          <p className="text-red-200 text-xs font-semibold flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> Customer allergic to: {declared.join(", ")}
          </p>
        </div>
      )}
      <div className="mt-3 space-y-1.5">
        {order.items.map((it) => {
          const itemAllergens = (it.allergens_snapshot || []).map((a) => a.name || a).filter(Boolean);
          const matched = itemAllergens.filter((a) => declared.includes(a));
          return (
            <div key={it.id} className="text-sm">
              <p className="text-slate-100">
                <span className="font-bold text-white">{it.quantity}×</span> {it.item_name_snapshot}
              </p>
              {it.options_snapshot?.length > 0 && (
                <p className="text-slate-400 text-xs pl-5">
                  {it.options_snapshot.map((o) => (o.name && o.value ? `${o.name}: ${o.value}` : (o.value || o.name))).join(" · ")}
                </p>
              )}
              {it.notes && <p className="text-amber-300/90 text-xs pl-5">“{it.notes}”</p>}
              {matched.length > 0 && (
                <p className="text-red-300 text-xs pl-5 flex items-center gap-1 font-semibold" data-testid={`kds-item-allergen-${it.id}`}>
                  <AlertTriangle className="h-3 w-3" /> Allergen match: {matched.join(", ")}
                </p>
              )}
            </div>
          );
        })}
      </div>
      {allergyNote && (
        <p className="text-red-200 bg-red-500/15 rounded px-2 py-1 text-xs mt-2 flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Allergy note: {allergyNote}
        </p>
      )}
      {order.special_notes && <p className="text-amber-300 text-xs mt-2">Note: {order.special_notes}</p>}
      <Button data-testid={`kds-advance-${order.id}`} onClick={() => onAdvance(order)}
        className="w-full mt-3 h-11 bg-emerald-600 hover:bg-emerald-500 text-white">{cta}</Button>
    </div>
  );
}

export default function Kitchen() {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [prepMinutes, setPrepMinutes] = useState(25);
  const [restaurant, setRestaurant] = useState(null);
  const [now, setNow] = useState(Date.now());
  const printCfg = useRef({});
  const seenRef = useRef(null); // null until first load

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/orders`).then((r) => {
      const data = r.data;
      // Auto-print brand-new tickets (skip the very first load)
      const cfg = printCfg.current || {};
      const active = data.filter((o) => ["NEW", "ACCEPTED", "ALLERGY_REVIEW_REQUIRED", "NEEDS_REVIEW", "PREPARING", "READY"].includes(o.status));
      if (seenRef.current === null) {
        seenRef.current = new Set(active.map((o) => o.id));
      } else {
        const fresh = active.filter((o) => !seenRef.current.has(o.id));
        fresh.forEach((o) => seenRef.current.add(o.id));
        if (cfg.enabled && cfg.auto_print) {
          fresh.forEach((o) => { try { printReceipt(o, cfg, restaurant || {}); } catch { /* noop */ } });
        }
      }
      setOrders(data);
    }).catch(() => {});
  }, [rid, restaurant]);

  useEffect(() => {
    api.get(`/restaurants/${rid}`).then((r) => setRestaurant(r.data)).catch(() => {});
    api.get(`/restaurants/${rid}/settings`).then((r) => {
      setPrepMinutes(r.data.average_prep_minutes ?? 25);
      printCfg.current = r.data.printing || {};
    }).catch(() => {});
    load();
    const poll = setInterval(load, 30000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, [rid, load]);

  useRealtimeOrders(rid, (data) => {
    if (data?.type === "order_created") {
      playChime();
      toast.success("New order received", { description: "A ticket just landed in the kitchen." });
    }
    load();
  });

  const printOrder = (order) => printReceipt(order, printCfg.current || {}, restaurant || {});

  const advance = async (order) => {
    const col = COLUMNS.find((c) => c.statuses.includes(order.status));
    const next = col ? col.next : "PREPARING";
    await api.patch(`/orders/${order.id}/status`, { status: next });
    toast.success(`${order.order_number} → ${pretty(next)}`);
    load();
  };

  const active = orders.filter((o) => ["NEW", "ACCEPTED", "ALLERGY_REVIEW_REQUIRED", "NEEDS_REVIEW", "PREPARING", "READY"].includes(o.status));

  const features = useFeatures(rid);
  if (features && !features.KDS) return <div className="min-h-screen bg-slate-900"><FeatureLocked label="Kitchen Display" /></div>;

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-700 bg-slate-950 px-5 h-16">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="kds-back" onClick={() => navigate(`/r/${rid}/dashboard`)}><ArrowLeft /></Button>
          <ChefHat className="h-6 w-6 text-emerald-400" />
          <div>
            <p className="font-bold leading-tight">Kitchen Display</p>
            <p className="text-xs text-slate-400">{restaurant?.name} · {active.length} active</p>
          </div>
        </div>
        <span className="font-mono text-slate-300">{new Date(now).toLocaleTimeString()}</span>
      </header>
      <div className="grid md:grid-cols-3 gap-4 p-4">
        {COLUMNS.map((col) => {
          const list = active.filter((o) => col.statuses.includes(o.status));
          return (
            <div key={col.key} data-testid={`kds-col-${col.key}`} className="rounded-xl bg-slate-800/40 p-3">
              <div className="flex items-center justify-between mb-3 px-1">
                <h2 className="font-bold text-slate-200">{col.label}</h2>
                <Badge className="bg-slate-700 text-slate-200 border-none">{list.length}</Badge>
              </div>
              {list.map((o) => (
                <Ticket key={o.id} order={o} prepMinutes={prepMinutes} now={now} onAdvance={advance} cta={col.cta} onPrint={printOrder} />
              ))}
              {list.length === 0 && <p className="text-slate-500 text-sm text-center py-8">No tickets</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
